import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';

type Asset = Database['public']['Tables']['assets']['Row'] & {
  asset_records: Database['public']['Tables']['asset_records']['Row'][];
};

const ScanRedirect = () => {
  const navigate = useNavigate();
  const { uid } = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAssetAndRedirect = async () => {
      if (!uid) {
        navigate('/scan', { replace: true });
        return;
      }

      // Get the complete URL
      const completeUrl = window.location.href;

      try {
        // First check if the user is logged in
        const { data: { session } } = await supabase.auth.getSession();
        
        // Check if the asset exists using the complete URL
        const { data: asset, error } = await supabase
          .from("assets")
          .select(`
            *,
            asset_records(
              *
            )
          `)
          .eq("qr_id", completeUrl)
          .single();

        if (error || !asset) {
          // If asset doesn't exist, redirect to appropriate scan page with the url
          if (session) {
            localStorage.setItem('pendingQrId', completeUrl);
            navigate('/scan', { replace: true });
          } else {
            localStorage.setItem('pendingQrId', completeUrl);
            navigate('/public/scan', { replace: true });
          }
          return;
        }

        // If asset exists but is private and user is not logged in
        if (asset.private && !session) {
          toast({
            variant: "destructive",
            title: "Private Asset",
            description: "This asset is private. Please log in to view it."
          });
          navigate('/auth', { replace: true });
          return;
        }

        // If everything is ok, redirect to appropriate scan page with the url
        if (session) {
          localStorage.setItem('pendingQrId', completeUrl);
          navigate('/scan', { replace: true });
        } else {
          localStorage.setItem('pendingQrId', completeUrl);
          navigate('/public/scan', { replace: true });
        }
      } catch (error) {
        console.error('Error checking asset:', error);
        navigate('/scan', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    checkAssetAndRedirect();
  }, [navigate, uid, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-500">Redirecting...</p>
      </div>
    </div>
  );
};

export default ScanRedirect; 